import { validateConditionalRules } from 'dashboard/lib/workflows/validation/validateConditionalStep';
import {
  fieldPathHasValidSchema,
  WorkflowValidationContext,
} from 'dashboard/lib/workflows/validation/validationContext';
import { WorkflowConfigError, WorkflowErrorType } from 'dashboard/models/workflow';
import {
  ActualWorkflowStep,
  FakeWorkflowStepType,
  WorkflowStepLoop,
  WorkflowStepLoopType,
} from 'dashboard/models/workflow-version';

export const validateLoopStep = (
  step: WorkflowStepLoop,
  validationContext: WorkflowValidationContext,
): WorkflowConfigError[] => {
  let errors: WorkflowConfigError[] = [];
  if (
    step.branch.filter(
      (step): step is ActualWorkflowStep => step.type !== FakeWorkflowStepType.MapOutput,
    ).length === 0
  ) {
    errors.push({
      token: step.token,
      message: 'Must have at least one step in its branch.',
      type: WorkflowErrorType.Publish,
    });
  }

  const stepContext = validationContext.getContextForStep(step.token);

  const maxIterations = validationContext.featureFlags[
    'global:increase-sequential-loop-max-iterations-limit'
  ]
    ? 1000
    : 100;

  switch (step.settings.type) {
    case WorkflowStepLoopType.ForEach: {
      if (!step.settings['array-path']) {
        errors.push({
          field: 'array-path',
          token: step.token,
          message: 'Must specify list to loop over',
          type: WorkflowErrorType.Publish,
        });
      }
      const { valid, errorMessages } = fieldPathHasValidSchema(
        step.settings['array-path'],
        stepContext,
        validationContext,
      );
      if (step.settings['array-path'] && !valid) {
        errors.push({
          field: 'array-path',
          token: step.token,
          message: `Invalid path: ${errorMessages.join(', ')}`,
          type: WorkflowErrorType.Publish,
        });
      }
      break;
    }
    case WorkflowStepLoopType.While: {
      if ((Number(step.settings['max-iterations']) ?? 0) < 1) {
        errors.push({
          field: 'max-iterations',
          token: step.token,
          message: 'Must be greater than 0',
          type: WorkflowErrorType.Publish,
        });
      }

      if ((Number(step.settings['max-iterations']) ?? 0) > maxIterations) {
        errors.push({
          field: 'max-iterations',
          token: step.token,
          message: `Must not exceed ${maxIterations}`,
          type: WorkflowErrorType.Publish,
        });
      }

      errors = errors.concat(
        validateConditionalRules(
          step.settings.conditional,
          validationContext,
          step.token,
          stepContext.jsonSchema,
        ),
      );

      break;
    }
    case WorkflowStepLoopType.Map: {
      if (!step.settings['array-path']) {
        errors.push({
          field: 'array-path',
          token: step.token,
          message: 'Must specify list to loop over',
          type: WorkflowErrorType.Publish,
        });
      }

      if (step.settings['array-path']) {
        const { valid, errorMessages } = fieldPathHasValidSchema(
          step.settings['array-path'],
          stepContext,
          validationContext,
        );
        if (!valid) {
          errors.push({
            field: 'array-path',
            token: step.token,
            message: `Invalid path: ${errorMessages.join(', ')}`,
            type: WorkflowErrorType.Publish,
          });
        }
      }

      if (!step.settings['item-path']) {
        errors.push({
          field: 'item-path',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          token: step.settings['output-step-token'],
          message: 'Must specify map output result',
          type: WorkflowErrorType.Publish,
        });
      }
      if (step.settings['item-path']) {
        const mapOutputStep = step.branch.find(
          (branchStep) => branchStep.type === FakeWorkflowStepType.MapOutput,
        );
        if (!mapOutputStep) {
          break;
        }

        const mapOutputStepContext = validationContext.getContextForStep(mapOutputStep.token);
        const { valid, errorMessages } = fieldPathHasValidSchema(
          step.settings['item-path'],
          mapOutputStepContext,
          validationContext,
        );
        if (!valid) {
          errors.push({
            field: 'item-path',
            token: step.settings['output-step-token'] || '',
            message: `Invalid path: ${errorMessages.join(', ')}`,
            type: WorkflowErrorType.Publish,
          });
        }
      }

      break;
    }
  }

  return errors;
};
